import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { KsefMonitoringApiService } from 'shared/services/api/ksef-monitoring.api.service';

interface InvalidNipUser {
  userId: string;
  email: string;
  companyName: string;
  currentNip: string;
  cleanedNip: string | null;
  canAutoClean: boolean;
  isCleaningInProgress?: boolean;
  isCleaned?: boolean;
}

@Component({
  selector: 'app-nip-verification',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './nip-verification.component.html',
  styleUrls: ['./nip-verification.component.scss']
})
export class NipVerificationComponent implements OnInit, OnDestroy {

  isLoading = true;
  users: InvalidNipUser[] = [];
  totalCount = 0;
  canAutoCleanCount = 0;
  cannotAutoCleanCount = 0;
  cleanedCount = 0;
  isCleaningAll = false;

  private _unsubscribeAll = new Subject<void>();

  constructor(private ksefMonitoringApi: KsefMonitoringApiService) { }

  ngOnInit(): void {
    this.loadInvalidNips();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadInvalidNips(): void {
    this.isLoading = true;
    this.ksefMonitoringApi.getInvalidNips()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res: any) => {
          const data = res.data;
          this.users = data.users || [];
          this.totalCount = data.total || 0;
          this.canAutoCleanCount = data.canAutoClean || 0;
          this.cannotAutoCleanCount = data.cannotAutoClean || 0;
          this.cleanedCount = 0;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  cleanNip(user: InvalidNipUser): void {
    if (!user.canAutoClean || user.isCleaningInProgress || user.isCleaned) return;

    user.isCleaningInProgress = true;
    this.ksefMonitoringApi.cleanNip(user.userId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res: any) => {
          user.isCleaningInProgress = false;
          if (res.data?.changed) {
            user.isCleaned = true;
            user.currentNip = res.data.cleanedNip;
            this.cleanedCount++;
          }
        },
        error: () => {
          user.isCleaningInProgress = false;
        }
      });
  }

  cleanAllNips(): void {
    const usersToClean = this.users.filter(u => u.canAutoClean && !u.isCleaned && !u.isCleaningInProgress);
    if (usersToClean.length === 0) return;

    this.isCleaningAll = true;

    let index = 0;
    const cleanNext = () => {
      if (index >= usersToClean.length) {
        this.isCleaningAll = false;
        return;
      }

      const user = usersToClean[index];
      user.isCleaningInProgress = true;

      this.ksefMonitoringApi.cleanNip(user.userId)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: (res: any) => {
            user.isCleaningInProgress = false;
            if (res.data?.changed) {
              user.isCleaned = true;
              user.currentNip = res.data.cleanedNip;
              this.cleanedCount++;
            }
            index++;
            cleanNext();
          },
          error: () => {
            user.isCleaningInProgress = false;
            index++;
            cleanNext();
          }
        });
    };

    cleanNext();
  }

  get remainingToClean(): number {
    return this.users.filter(u => u.canAutoClean && !u.isCleaned).length;
  }
}
